import { apiFetch } from "../api/client";
import { Play } from "../api/types";
import { cropToRect, DEFAULT_CROP, normalizeCrop, PlayCrop } from "./crop";
import { playToSvg } from "./playToSvg";
import { svgToPng } from "./svgToPng";

export interface RenderedPlayPng {
  png: string;
  width: number;
  height: number;
  playWidth: number;
  playHeight: number;
}

export interface RenderPlayPngOptions {
  scale?: number;
  crop?: PlayCrop;
  /** Skip API fetch when caller already has the full play document */
  skipFetch?: boolean;
}

function isDefaultCrop(crop: PlayCrop): boolean {
  const n = normalizeCrop(crop);
  return n.zoom === 1 && n.centerX === 0.5 && n.centerY === 0.5;
}

export async function renderPlayPng(
  play: Play,
  options: RenderPlayPngOptions = {}
): Promise<RenderedPlayPng> {
  const fullPlay = options.skipFetch ? play : await apiFetch<Play>(`/play/${play._id}`);
  const { svg, fieldDataUri, width, height } = await playToSvg(fullPlay);
  const crop = normalizeCrop(options.crop ?? DEFAULT_CROP);
  const hasCrop = !isDefaultCrop(crop);
  const cropRect = hasCrop ? cropToRect(crop, width, height) : undefined;
  const scale = options.scale ?? (hasCrop ? Math.min(2 * crop.zoom, 8) : 2);
  const png = await svgToPng(svg, width, height, scale, fieldDataUri, cropRect);

  if (!png || png.length < 100) {
    throw new Error("Render produced empty image");
  }

  const outW = hasCrop ? cropRect!.w : width;
  const outH = hasCrop ? cropRect!.h : height;

  return { png, width: outW, height: outH, playWidth: width, playHeight: height };
}
